import style from './style.module.css';
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import {
  PopConfirm,
  Button,
  UploadFile,
  Notification,
  Col,
  Row,
} from 'fave-ui';
import Form from '../../../../components/common/form/Form';
import { Input } from '../../../../components/common/form/Input';
import SubmitButton from '../../../../components/common/form/SubmitButton';
import ImageUpload from '../../../../components/common/form/ImageUpload';
import {
  PrizeDataItem,
  useEditPrizeCampaignMutation,
} from '../../../../services/FaveArcade/useFetchPrizesListing';
import { appendFormData } from '../../../../utils/utilFunctions';

type EditPromoCodeFormFields = Pick<
  PrizeDataItem,
  | 'id'
  | 'campaign_name'
  | 'prize_name'
  | 'prize_subtitle'
  | 'prize_description'
  | 'validity_day'
  | 'asset_url'
> & { asset: UploadFile[] };

type PromoCodeProps = {
  initialData: PrizeDataItem;
};

const EditPromoCodeFormSchema = Yup.object().shape({
  campaign_name: Yup.string().required('Please give your prize a name'),
  prize_name: Yup.string().required('Please give your prize a name'),
  prize_subtitle: Yup.string().optional(),
  prize_description: Yup.string().required(
    'Please give your prize a description',
  ),
  asset: Yup.array()
    .of(Yup.mixed().nullable().required('An image is required'))
    .min(1, 'Please upload an image'),
  validity_day: Yup.number()
    .min(1)
    .required('Please enter enter the period in days'),
});

const successNotification = () =>
  Notification.success({
    message: 'Prize Campaign Updated!',
    description:
      'The prize campaign has been successfully updated. You can view it in the Prize Catalog.',
  });

const EditPromoCodeForm = ({ initialData }: PromoCodeProps) => {
  const navigate = useNavigate();
  const { mutateAsync: updatePrize } = useEditPrizeCampaignMutation({
    id: initialData?.id,
    type: 'promo_code',
  });

  const handleSubmit = async (formValues: EditPromoCodeFormFields) => {
    const payload = {
      ...formValues,
      asset: formValues?.asset[0].originFileObj,
    };

    // Remove asset if it is not edited (i.e. it is not a File object)
    if (!(payload.asset instanceof File)) {
      delete payload.asset;
    }

    const formData = appendFormData(payload);

    updatePrize(formData, {
      onSuccess: () => {
        successNotification();
        navigate('/fave_arcade/prize_catalog');
      },
    });
  };

  const assetFiles: UploadFile[] = [
    {
      uid: '-1',
      name: 'image.png',
      url: initialData?.asset_url,
      thumbUrl: initialData?.asset_url,
    },
  ];

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{
        ...initialData,
        validity_day: initialData?.validity_day ?? 0,
        asset: assetFiles,
      }}
      validationSchema={EditPromoCodeFormSchema}
      validateOnMount
      validateOnChange
      validateOnSchemaChange
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Input
            name="campaign_name"
            label="* Campaign Name"
            placeholder="Enter campaign name"
            allowClear
            bordered
            style={{ marginBottom: 0 }}
          />
        </Col>
        <Col span={24}>
          <Input
            name="prize_name"
            label="* Prize Name"
            placeholder="Enter prize name"
            allowClear
            bordered
            style={{ marginBottom: 0 }}
          />
        </Col>
        <Col span={24}>
          <Input
            name="prize_subtitle"
            label="Prize Subtitle (optional)"
            placeholder="Enter prize subtitle"
            allowClear
            bordered
            style={{ marginBottom: 0 }}
          />
        </Col>
        <Col span={24}>
          <Input
            type="textArea"
            name="prize_description"
            label="* Prize Description"
            placeholder="Enter prize description"
            bordered
            style={{ marginBottom: 0 }}
          />
        </Col>
        <Col span={24}>
          <Input
            type="number"
            name="validity_day"
            label="* Validity Period"
            placeholder="Enter number of days"
            allowClear
            bordered
            style={{ marginBottom: 0 }}
          />
        </Col>
        <Col span={12}>
          <ImageUpload
            name="asset"
            label="* Asset (image)"
            types={['image/jpeg', 'image/png']}
            imgSize={2}
            defaultFileList={assetFiles}
          />
        </Col>

        <Col span={24}>
          <SubmitButton disableIfNotValid disableIfNotDirty>
            {(_, isSubmitting) => (isSubmitting ? 'Updating...' : 'Update')}
          </SubmitButton>
          <PopConfirm
            title="Are you sure you want to cancel?"
            onConfirm={() => navigate('/fave_arcade/prize_catalog')}
            okText="Yes, I am sure"
            cancelText="Go Back"
          >
            <Button type="primary" size="large" className={style.mgLeft}>
              Cancel
            </Button>
          </PopConfirm>
        </Col>
      </Row>
    </Form>
  );
};

export default EditPromoCodeForm;
